import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "margin", "marginX", "marginY", "marginLeft", "marginRight", "marginTop", "marginBottom", "padding", "paddingX", "paddingY", "paddingLeft", "paddingRight", "paddingTop", "paddingBottom"],
  _excluded2 = ["children", "title"];
import PropTypes from 'prop-types';
import React, { Children, cloneElement, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { ICON_STATUS_TYPES } from '../../constants';
import { DetailedIconMixin } from '../../mixins/components/DetailedIconMixin';
import { identifier } from '../../util/identifier';

/**
 * DetailedIcon is a generic wrapper used to provide icon functionality and styling.
 * Unless you are creating your own icons, you will generally want to use one of the
 * individual detailed icon components.
 *
 * @related [`Icon`]
 * @since 8.40.0
 */

var DetailedIcon = /*#__PURE__*/styled(function (_ref) {
  var children = _ref.children,
    margin = _ref.margin,
    marginX = _ref.marginX,
    marginY = _ref.marginY,
    marginLeft = _ref.marginLeft,
    marginRight = _ref.marginRight,
    marginTop = _ref.marginTop,
    marginBottom = _ref.marginBottom,
    padding = _ref.padding,
    paddingX = _ref.paddingX,
    paddingY = _ref.paddingY,
    paddingLeft = _ref.paddingLeft,
    paddingRight = _ref.paddingRight,
    paddingTop = _ref.paddingTop,
    paddingBottom = _ref.paddingBottom,
    rest = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/cloneElement(Children.only(children), rest);
}).withConfig({
  displayName: "DetailedIcon",
  componentId: "c11n-8-107-0__sc-1u552bz-0"
})(["", ""], DetailedIconMixin);
DetailedIcon.displayName = 'DetailedIcon';
DetailedIcon.propTypes = {
  /**
   * For presentational icons, this should be set to `true`.
   * For contextually meaningful icons, this should be set to `false`
   * and a `<title>` and `<desc>` should be added.
   *
   * @deprecated Use the `title` prop instead
   */
  'aria-hidden': PropTypes.oneOf(['true', 'false']),
  /**
   * If `aria-hidden="false"`, you need to add a `<title>` and `<desc>` and point to them with the
   * `aria-describedby` attribute to make the icon accessible to screen readers.
   *
   * @deprecated Use the `title` prop instead
   */
  'aria-describedby': PropTypes.string,
  /**
   * The SVG [`focusable`](https://www.w3.org/TR/SVGTiny12/interact.html#focusable-attr)
   * attribute.
   */
  'focusable': PropTypes.oneOf(['true', 'false', 'auto']),
  /**
   * The role is set to "img" by default to exclude all child content from the accessibility tree.
   */
  'role': PropTypes.string,
  /**
   * The status prop allows you to change the color wave.
   * Currently it's default is `info` which is a blue monochrome + accent and
   * `success` which is a green monochrome + accent.
   */
  'status': PropTypes.oneOf(Object.values(ICON_STATUS_TYPES)),
  /**
   * Creates an accessible label for the icon for contextually meaningful icons, and sets
   * the appropriate `aria` attributes. Icons are hidden from screen readers by default
   * without this prop.
   *
   * Note: specifying `aria-labelledby`, `aria-hidden`, or `children` manually while using
   * this prop may produce accessibility errors. This prop is only available on prebuilt icons
   * within Constellation.
   */
  'title': PropTypes.string
};
DetailedIcon.defaultProps = {
  'aria-hidden': 'true',
  'focusable': 'false',
  'role': 'img',
  'status': ICON_STATUS_TYPES.info
};
var generateDisplayName = function generateDisplayName(target) {
  var displayName;
  if (target.displayName) {
    // eslint-disable-next-line prefer-destructuring
    displayName = target.displayName;
  } else if (target.name) {
    // make various icon export patterns yield consistent output
    displayName = target.name.replace(/^(?:DetailedIcon)?(.*?)(?:SVG)?$/i, 'DetailedIcon$1');
  }
  return displayName;
};
export function withDetailedIcon(Component) {
  var DetailedIconComponent = function DetailedIconComponent(_ref2) {
    var children = _ref2.children,
      title = _ref2.title,
      props = _objectWithoutProperties(_ref2, _excluded2);
    var id = useRef(identifier());
    var iconChildren = useMemo(function () {
      if (children) {
        return children;
      }
      return title ? /*#__PURE__*/React.createElement("title", {
        id: id.current
      }, title) : null;
    }, [children, title, id]);
    return /*#__PURE__*/React.createElement(DetailedIcon, _extends({
      "aria-hidden": title ? 'false' : 'true',
      "aria-labelledby": title ? id.current : undefined
    }, props), /*#__PURE__*/React.createElement(Component, null, iconChildren));
  };
  DetailedIconComponent.propTypes = process.env.NODE_ENV !== "production" ? {
    children: PropTypes.node
  } : {};
  DetailedIconComponent.displayName = generateDisplayName(Component);
  return DetailedIconComponent;
}

/** @component */
export { DetailedIcon };